<template>
  <teleport to="body">
    <div v-if="showTopLoader" class="global-api-loader" aria-hidden="true">
      <div class="global-api-loader__bar"></div>
    </div>

    <div
      v-if="activity.importActive"
      class="import-blocking-overlay"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="import-blocking-title"
    >
      <div class="import-blocking-panel">
        <div class="spinner-chase import-blocking-spinner" aria-hidden="true">
          <div class="chase-dot"></div>
          <div class="chase-dot"></div>
          <div class="chase-dot"></div>
          <div class="chase-dot"></div>
          <div class="chase-dot"></div>
          <div class="chase-dot"></div>
        </div>
        <h5 id="import-blocking-title" class="mb-2">{{ activity.message }}</h5>
        <p class="text-muted mb-3">{{ activity.detail }}</p>

        <div class="progress animated-progress progress-sm bg-primary-subtle import-progress mb-2">
          <div
            class="progress-bar progress-bar-striped progress-bar-animated bg-success"
            :class="{ 'import-progress-indeterminate': activity.progress === null }"
            :style="{ width: progressWidth }"
            role="progressbar"
            :aria-valuenow="activity.progress || undefined"
            aria-valuemin="0"
            aria-valuemax="100"
          ></div>
        </div>

        <div class="d-flex justify-content-between text-muted small">
          <span>{{ progressLabel }}</span>
          <span>{{ elapsedLabel }}</span>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import { requestActivity } from '@/services/requestActivity';

export default {
  name: 'GlobalRequestOverlay',
  data() {
    return {
      activity: requestActivity,
      topLoaderVisible: false,
      elapsedSeconds: 0,
      delayTimer: null,
      elapsedTimer: null,
    };
  },
  computed: {
    showTopLoader() {
      return this.topLoaderVisible && !this.activity.importActive;
    },
    progressWidth() {
      return this.activity.progress === null ? '100%' : `${this.activity.progress}%`;
    },
    progressLabel() {
      if (this.activity.progress === null) {
        return 'Processing file on server';
      }
      if (this.activity.progress >= 100) {
        return 'Upload complete. Processing file on server';
      }
      return `${this.activity.progress}% uploaded`;
    },
    elapsedLabel() {
      if (!this.activity.importActive) return '';
      const minutes = Math.floor(this.elapsedSeconds / 60);
      const seconds = this.elapsedSeconds % 60;
      return minutes > 0 ? `${minutes}m ${seconds}s elapsed` : `${seconds}s elapsed`;
    },
  },
  watch: {
    'activity.activeRequests'(count) {
      clearTimeout(this.delayTimer);
      if (count > 0) {
        this.delayTimer = setTimeout(() => {
          this.topLoaderVisible = true;
        }, 250);
      } else {
        this.topLoaderVisible = false;
      }
    },
    'activity.importActive'(active) {
      clearInterval(this.elapsedTimer);
      if (!active) {
        this.elapsedSeconds = 0;
        return;
      }
      this.elapsedSeconds = 0;
      this.elapsedTimer = setInterval(() => {
        this.elapsedSeconds += 1;
      }, 1000);
    },
  },
  beforeUnmount() {
    clearTimeout(this.delayTimer);
    clearInterval(this.elapsedTimer);
  },
};
</script>

<style scoped>
.global-api-loader {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2050;
  height: 3px;
  background: rgba(13, 110, 253, 0.12);
}

.global-api-loader__bar {
  width: 42%;
  height: 100%;
  background: #198754;
  animation: api-loader-slide 1s ease-in-out infinite;
}

.import-blocking-overlay {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(12, 18, 28, 0.72);
}

.import-blocking-panel {
  width: min(460px, 100%);
  padding: 28px;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.28);
  text-align: center;
}

.import-blocking-spinner {
  margin: 0 auto 18px;
}

.import-progress {
  height: 10px;
  border-radius: 999px;
}

.import-progress-indeterminate {
  width: 100%;
}

@keyframes api-loader-slide {
  0% {
    transform: translateX(-100%);
  }
  50% {
    transform: translateX(90%);
  }
  100% {
    transform: translateX(240%);
  }
}

</style>
